import React, {Suspense} from 'react';

import {ErrorBoundary} from '../ErrorBoundary';
import {
  AgentChatAvailabilityContext,
  useComputeAgentChatAvailability,
} from './useAgentChatAvailability';

// Lazy-load the inner provider to code-split `@assistant-ui/react` and runtime
// dependencies. This is only loaded when the feature is fully enabled.
const AgentChatRuntimeProvider = React.lazy(
  () => import('./AgentChatRuntimeProvider')
);

/**
 * Top-level provider for the Agent Chat feature.
 *
 * Gates on feature flags and org settings, then code-splits the heavy runtime
 * dependencies (assistant-ui, LLM proxy client) behind `React.lazy`.
 */
export function AgentChatProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const availability = useComputeAgentChatAvailability();

  return (
    <AgentChatAvailabilityContext.Provider value={availability}>
      {availability.isAvailable ? (
        <ErrorBoundary renderError={() => <>{children}</>}>
          <Suspense fallback={<>{children}</>}>
            <AgentChatRuntimeProvider>{children}</AgentChatRuntimeProvider>
          </Suspense>
        </ErrorBoundary>
      ) : (
        <>{children}</>
      )}
    </AgentChatAvailabilityContext.Provider>
  );
}
